import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { ApiError } from '@/shared/api/client';
import { phoneLogin, pollOtp, startOtp } from './api';
import { PhoneField } from './PhoneField';
import type { LoginResponseLike } from '@/shared/auth/storage';

interface Props {
  onLoggedIn: (res: LoginResponseLike) => void;
}

type Stage = 'phone' | 'code';

function fmtCountdown(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * SMS-ээр нэвтрэх — verify.mn **inbound** урсгал (CLAUDE.md §3.3.1):
 * систем код илгээхгүй, харин ХЭРЭГЛЭГЧ доорх кодыг тусгай дугаар руу
 * өөрийн утаснаасаа SMS-ээр илгээнэ. Frontend статусыг poll хийж хүлээнэ.
 */
export function OtpStep({ onLoggedIn }: Props) {
  const [stage, setStage] = useState<Stage>('phone');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [destNumber, setDestNumber] = useState('');
  const [instruction, setInstruction] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [copied, setCopied] = useState(false);
  const sessionIdRef = useRef<string | null>(null);
  const expiresAtRef = useRef<number>(0);
  const pollRef = useRef<number | null>(null);
  const tickRef = useRef<number | null>(null);

  function clearTimers() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }

  useEffect(() => () => clearTimers(), []);

  function reset(reason: string | null) {
    clearTimers();
    sessionIdRef.current = null;
    setStage('phone');
    setBusy(false);
    setCopied(false);
    if (reason) setError(reason);
  }

  async function onSubmitPhone(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\d{8}$/.test(phone)) {
      setError('Утасны дугаар буруу байна (8 оронтой).');
      return;
    }
    setBusy(true);
    try {
      const r = await startOtp(phone);
      sessionIdRef.current = r.SessionId;
      expiresAtRef.current = new Date(r.ExpiresAt).getTime();
      const total = Math.max(1, Math.floor((expiresAtRef.current - Date.now()) / 1000));
      setTotalSeconds(total);
      setSecondsLeft(total);
      const instr = r.DisplayInstruction ?? '';
      const code4 = instr.match(/\b(\d{4})\b/);
      const dest = instr.match(/\b(\d{5,7})\b/);
      setSmsCode(code4?.[1] ?? '');
      setDestNumber(dest?.[1] ?? '');
      setInstruction(code4 ? '' : instr);
      setStage('code');
      tickRef.current = window.setInterval(() => {
        const left = Math.max(0, Math.floor((expiresAtRef.current - Date.now()) / 1000));
        setSecondsLeft(left);
      }, 1000);
      pollRef.current = window.setInterval(async () => {
        const id = sessionIdRef.current;
        if (!id) return;
        try {
          const s = await pollOtp(id);
          if (s.SessionStatus === 'VERIFIED') {
            clearTimers();
            const login = await phoneLogin(phone, id);
            onLoggedIn(login);
          } else if (s.SessionStatus === 'EXPIRED' || s.SessionStatus === 'FAILED') {
            reset('Хугацаа дууссан. Дахин оролдоно уу.');
          }
        } catch (err) {
          if (err instanceof ApiError && err.status >= 500) {
            // graceful: үргэлжлүүлэн polling.
          }
        }
      }, 2500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Сервертэй холбогдож чадсангүй.');
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(smsCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard хориотой орчинд чимээгүй */
    }
  }

  if (stage === 'phone') {
    return (
      <form className="auth-form" onSubmit={onSubmitPhone}>
        <PhoneField value={phone} onChange={setPhone} autoFocus />
        {error ? (
          <div className="auth-error" role="alert">
            <i className="bi bi-exclamation-circle" /> {error}
          </div>
        ) : null}
        <Button type="submit" className="auth-submit" disabled={busy} icon="chat-dots">
          {busy ? 'Бэлтгэж байна…' : 'SMS-ээр нэвтрэх'}
        </Button>
        <p className="if-hint" style={{ textAlign: 'center', margin: 0 }}>
          Дараагийн алхамд танд 4 оронтой код өгнө. Та түүнийг заасан дугаар руу
          утаснаасаа SMS-ээр илгээнэ.
        </p>
      </form>
    );
  }

  const pct = totalSeconds > 0 ? Math.round((secondsLeft / totalSeconds) * 100) : 0;
  const smsHref = destNumber ? `sms:${destNumber}?body=${encodeURIComponent(smsCode)}` : null;

  return (
    <div className="auth-otp">
      <div className="auth-otp-steps">
        <div className="auth-otp-step">
          <span className="auth-otp-num">1</span>
          <span>
            <b>{phone}</b> дугаартай утсаасаа доорх кодыг{' '}
            {destNumber ? (
              <>
                <b>{destNumber}</b> дугаар руу
              </>
            ) : (
              'заасан дугаар руу'
            )}{' '}
            SMS-ээр илгээнэ үү.
          </span>
        </div>
        <div className="auth-otp-step">
          <span className="auth-otp-num">2</span>
          <span>SMS хүлээн авмагц систем таныг автоматаар нэвтрүүлнэ.</span>
        </div>
      </div>

      {smsCode ? (
        <div className="auth-otp-code">
          <div>
            <div className="auth-otp-code-label">Илгээх код</div>
            <div className="auth-otp-code-value">{smsCode}</div>
            {destNumber ? (
              <div className="auth-otp-dest">
                Хүлээн авагч: <b>{destNumber}</b>
              </div>
            ) : null}
          </div>
          <Button
            variant="outline"
            className="auth-otp-copy ts-btn-sm"
            icon={copied ? 'check2' : 'clipboard'}
            onClick={copyCode}
          >
            {copied ? 'Хуулагдлаа' : 'Хуулах'}
          </Button>
        </div>
      ) : null}
      {instruction ? <div className="if-hint">{instruction}</div> : null}

      {smsHref ? (
        <a className="ts-btn-primary auth-submit auth-otp-sms" href={smsHref}>
          <i className="bi bi-send" /> SMS апп нээж илгээх
        </a>
      ) : null}

      <div>
        <div className="auth-otp-wait" role="status" aria-live="polite">
          <span className="auth-otp-dot" />
          Баталгаажуулалтыг хүлээж байна…
          <span className="auth-otp-timer">{fmtCountdown(secondsLeft)}</span>
        </div>
        <div className="auth-otp-bar" aria-hidden="true" style={{ marginTop: 6 }}>
          <span style={{ width: `${pct}%` }} />
        </div>
      </div>

      {error ? (
        <div className="auth-error" role="alert">
          <i className="bi bi-exclamation-circle" /> {error}
        </div>
      ) : null}
      <Button variant="ghost" icon="arrow-left" onClick={() => reset(null)} style={{ width: '100%' }}>
        Дугаар солих / цуцлах
      </Button>
    </div>
  );
}
